import { Injectable } from '@angular/core';
import { Order } from '@datorama/akita';
import { Notification } from '@lycoris-nubila/ngx-lycoris';
import { TranslateService } from '@ngx-translate/core';
import { uniqBy } from 'lodash-es';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { ApplicationAdapter } from 'application/application.adapter';
import { NotificationDomain } from 'application/common/model/notification-domain';
import { AppPushApiPort } from 'application/common/port/app-push-api.port';
import { TaskType } from 'task/model/task.type';
import { CreateUserTaskExportTaskCmd } from 'user/command/userTaskExportTask/create-user-task-export-task.cmd';
import { IamUserTaskExportTaskOutput } from 'user/model/iam-user-task-export-task.output';
import { UserTaskExportTaskSort } from 'user/model/user-task-export-task.sort';
import { UserApiPort } from 'user/port/user-api.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { UserSessionPort } from 'user/port/user-session.port';
import { UserTaskExportTaskRepository } from 'user/taskExportTask/user-task-export-task.repository';

@Injectable({ providedIn: 'root' })
export class UserTaskExportTaskCommandHandler extends ApplicationAdapter {
	constructor(
		translateService: TranslateService,
		private _userApiPort: UserApiPort,
		private _appPushApiPort: AppPushApiPort,
		private _userSessionPort: UserSessionPort,
		private _userApplicationPort: UserApplicationPort,
		private _userTaskExportTaskRepository: UserTaskExportTaskRepository
	) {
		super(translateService);

		this._appPushApiPort
			.observeDomainMessage<string>(NotificationDomain.USER_TASK_EXPORT_TASK)
			.pipe(tap(() => this._commandHandled$.next(Date.now())))
			.subscribe((message) => this._handleUserTaskExportTaskNotification(message));
	}

	private _commandHandled$ = new BehaviorSubject(Date.now());

	public get commandHandled$(): Observable<number> {
		return this._commandHandled$;
	}

	public createUserTaskExportTask(
		cmd: CreateUserTaskExportTaskCmd
	): Observable<string | Error> {
		return this._userApiPort.createUserTaskExportTask(cmd).pipe(
			tap(
				(userTaskExportTask) =>
					userTaskExportTask && this.saveUserTaskExportTasks([userTaskExportTask])
			),
			switchMap((userTaskExportTask) =>
				!userTaskExportTask
					? throwError(
							Error(this._buildInfrastructureErrorMessage('createUserTaskExportTask'))
					  )
					: of(userTaskExportTask.id)
			),
			catchError(() =>
				of(Error(this._buildInfrastructureErrorMessage('fetchModuleIllustrations')))
			)
		);
	}

	public saveUserTaskExportTasks(
		userTaskExportTasks: IamUserTaskExportTaskOutput[]
	): void {
		const filteredUserTaskExportTasks = uniqBy(
			userTaskExportTasks.filter((c) => !!c),
			'id'
		);

		this._userApplicationPort.saveUsers(
			filteredUserTaskExportTasks.map((userExportTask) => userExportTask.user.id)
		);

		this._userApplicationPort.saveUsers(
			filteredUserTaskExportTasks.map(
				(userExportTask) => userExportTask.exportedTasksParticipant.id
			)
		);

		this._userTaskExportTaskRepository.saveUserTaskExportTasks(
			filteredUserTaskExportTasks
		);
	}

	private _handleUserTaskExportTaskNotification<T>(
		message: Notification<NotificationDomain, T>
	): void {
		if (message.data != null) {
			this._userApiPort
				.searchUserTaskExportTask({
					userId: message.entityId,
					sort: UserTaskExportTaskSort.CREATION,
					exportedTasksType: message.data as TaskType,
					offset: 0,
					limit: 50,
				})
				.pipe(tap((userTaskExportTasks) => {}))
				.toPromise()
				.then((userTaskExportTasks) =>
					this.saveUserTaskExportTasks(userTaskExportTasks)
				);
		}
	}
}
